import React from 'react';
import { useCurrentEditor } from '@tiptap/react';

import { readFileAsDataUrl } from '~/components/core/Editor/extensions/EmbedImages';
import type { DisplayConfigProps, SuggestionOption } from '~/components/core/Editor/types';
import type { onUploadFinishFunc } from '~/components/core/Templates/UploadImageDialog';

import BulletList from './alignment/BulletList';
import OrderList from './alignment/OrderList';
import TextAlign from './alignment/TextAlign';
import HighlightComponent from './color/Highlight';
import TextColor from './color/TextColor';
import ConditionalSectionButton from './conditional/ConditionalSectionButton';
import FontFamily from './font/FontFamily';
import FontSize from './font/FontSize';
import HeadingSelector from './font/HeadingSelector';
import ImagesComponent from './images/Images';
import Link from './link/Link';
import SuggestionComponent from './suggestion/SuggestionMenuButton';
import Bold from './text/Bold';
import Italic from './text/Italic';
import Strike from './text/Strike';
import Underline from './text/Underline';

interface MenuBarProps {
  suggestionOptions: SuggestionOption[];
  displayConfig: DisplayConfigProps;
  onUploadFinish?: onUploadFinishFunc;
}

const MenuBar: React.FC<MenuBarProps> = ({ displayConfig, suggestionOptions, onUploadFinish }) => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <div className="inline-flex w-full border-0 border-b-1 border-solid border-slate-600 p-4">
      {displayConfig.headings ? <HeadingSelector /> : null}
      {displayConfig.font ? (
        <>
          <FontFamily />
          <FontSize />
        </>
      ) : null}
      {displayConfig.textColor ? (
        <>
          <TextColor />
          <HighlightComponent />
        </>
      ) : null}
      {displayConfig.textStyle ? (
        <>
          <Bold />
          <Italic />
          <Underline />
          <Strike />
        </>
      ) : null}
      {displayConfig.align || displayConfig.lists ? (
        <>
          <OrderList />
          <BulletList />
        </>
      ) : null}
      {displayConfig.align ? <TextAlign /> : null}
      {displayConfig.link ? <Link /> : null}
      {displayConfig.images ? (
        <ImagesComponent
          onUploadFinish={onUploadFinish ?? (displayConfig.embedImages ? readFileAsDataUrl : undefined)}
          // An embedded file is kept only if it is one of the types the editor embeds on paste and drop.
          accept={!onUploadFinish && displayConfig.embedImages ? displayConfig.embedImages.join(',') : undefined}
        />
      ) : null}
      {displayConfig.conditionalSections ? <ConditionalSectionButton /> : null}
      {displayConfig.suggestions ? <SuggestionComponent options={suggestionOptions} buttonClassName="ml-auto" /> : null}
    </div>
  );
};

export default MenuBar;
